import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, Info, DollarSign } from 'lucide-react';

import RegistrationDebug from '../../../../components/debug/RegistrationDebug';
import GlowingCard from '../../../../components/ui/GlowingCard';
import '../RegistrationFlow.css';

// Modern card components for better organization
const InfoCard = ({ isDarkMode, icon: Icon, title, children, className = "" }) => (
  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50 border border-white/5' : 'bg-white border border-gray-200 shadow-sm'} ${className}`}>
    <div className="flex items-center mb-3">
      <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
        <Icon className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
      </div>
      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
    </div>
    {children}
  </div>
);

// Event content component with streamlined layout
const EventContent = ({ isDarkMode, event, formatDate, formatCurrency, pricingSettings, criteriaSettings }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {/* Event Details Card */}
    <InfoCard isDarkMode={isDarkMode} icon={MapPin} title="Event Details">
      <div className="space-y-4">
        {/* Location and Date side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h4 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Location</h4>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{event.location}</p>
            {event.venue && typeof event.venue === 'object' ? (
              <>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{event.venue.name}</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{event.venue.address}</p>
              </>
            ) : (
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{event.venue}</p>
            )}
          </div>
          
          <div>
            <h4 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Date</h4>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {formatDate(event.startDate)} - {formatDate(event.endDate)}
            </p>
          </div>
        </div>

        {/* Notes section */}
        <div>
          <h4 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Notes</h4>
          {event.notes || event.description ? (
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {event.notes || event.description}
            </p>
          ) : (
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              No additional notes for this event.
            </p>
          )}
        </div>
      </div>
    </InfoCard>

    {/* Pricing Card */}
    <InfoCard isDarkMode={isDarkMode} icon={DollarSign} title="Pricing Information">
      {/* Pricing Note */}
      <div className={`mb-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <p>* All pricing shown is per dancer</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Competition Pricing (Routines) */}
        <div>
          <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Competition Pricing (Routine Types)</h4>
          {(() => {
            // Extract competition pricing from pricing settings
            const competitionPricing = pricingSettings?.competitionPricing || 
                                     pricingSettings?.pricing?.competitionPricing || 
                                     pricingSettings?.data?.competitionPricing || 
                                     [];
            
            if (competitionPricing && competitionPricing.length > 0) {
              return (
                <div className="space-y-2">
                  {competitionPricing.map((rule, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {rule.category || rule.routineType || `${rule.minDancers}-${rule.maxDancers || '+'} dancers`}:
                      </span>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                        {formatCurrency(parseFloat(rule.discountRate || rule.price || 0))}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
            
            return (
              <div className={`text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <p className="text-sm">Competition pricing will be available soon</p>
              </div>
            );
          })()}
        </div>

        {/* Workshop Levels (Dancers) */}
        <div>
          <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Workshop Pricing (Dancer Levels)</h4>
          {(() => {
            // Extract workshop levels from pricing settings
            const registrationPricing = pricingSettings?.registrationPricing || 
                                      pricingSettings?.pricing?.registrationPricing || 
                                      pricingSettings?.data?.registrationPricing || 
                                      [];
            
            if (registrationPricing && registrationPricing.length > 0) {
              return (
                <div className="space-y-2">
                  {registrationPricing.map((level, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {level.ageGroup || level.name || level.label}:
                      </span>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                        {formatCurrency(parseFloat(level.discountRate || level.price || level.cost || 0))}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
            
            return (
              <div className={`text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <p className="text-sm">Dancer pricing will be available soon</p>
              </div>
            );
          })()}
        </div>
      </div>
    </InfoCard>
  </div>
);

const EventOverview = ({ event, isDarkMode, onNext, updateRegistrationData, currentStep, totalSteps, registrationData, pricingSettings, criteriaSettings, renderNavigation }) => {
  const [overviewData, setOverviewData] = useState({
    reviewedOverview: false,
    timestamp: new Date().toISOString()
  });
  
  // Debug mode toggle
  const [showDebug, setShowDebug] = useState(false);
  
  if (!event) return null;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Determine the next step label based on event configuration
  const getNextStepLabel = () => {
    // Use flags from event.eventTypeInfo passed down from RegistrationFlow
    const typeInfo = event?.eventTypeInfo || {}; // Default to empty object
    
    // Determine based on the event type features what the next step would be
    if (typeInfo.hasCompetition) {
      return "Continue to Routines";
    } else if (typeInfo.hasConvention) {
      return "Continue to Dancers";
    } else if (typeInfo.hasTeachers) {
      return "Continue to Teachers";
    } else if (typeInfo.hasObservers) {
      return "Continue to Observers";
    } else {
      // If none of the optional steps are included, the next is Review
      return "Continue to Review";
    }
  };
  
  const handleContinue = () => {
    // Mark as reviewed and save progress before continuing
    const updatedData = {
      ...overviewData,
      reviewedOverview: true,
      timestamp: new Date().toISOString()
    };
    
    // Save to DB via RegistrationFlow's handleNext and continue to next step
    // Pass true to indicate this step is complete
    onNext(updatedData, true);
  };

  return (
    <div className="min-h-full w-full">
      {/* Fixed Header - Removed container class to match width of header */}
      <div className={`p-4 md:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h2 className={`responsive-heading font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Calendar className="h-6 w-6 mr-2 text-blue-500" />
          Event Overview
        </h2>
        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Review the event details before proceeding with your registration
        </p>
      </div>
      
      {/* Scrollable Content */}
      <div className="p-4 md:p-6 space-y-6">
        {/* Event Content - No longer needs wrapper card */}
        <EventContent 
          isDarkMode={isDarkMode} 
          event={event} 
          formatDate={formatDate} 
          formatCurrency={formatCurrency}
          pricingSettings={pricingSettings}
          criteriaSettings={criteriaSettings}
        />

        {/* Registration Progress Indicator */}
        {isDarkMode ? (
          <GlowingCard
            glowColor="from-blue-600/10 via-indigo-600/10 to-purple-600/10"
            glowIntensity="blur-lg"
            cardOpacity="bg-[#1a1f2d]/80"
            className="p-4"
          >
            <div className="flex items-center">
              <div className="p-2 rounded-lg mr-3 bg-blue-500/10">
                <Info className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <span className="text-blue-400 font-medium">
                  Registration Progress: Step {currentStep + 1} of {totalSteps}
                </span>
                <p className="mt-1 text-sm text-blue-300">
                  Your registration is automatically saved at each step. You can return anytime to continue.
                </p>
              </div>
            </div>
          </GlowingCard>
        ) : (
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center">
              <div className="p-2 rounded-lg mr-3 bg-blue-100">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <span className="text-blue-700 font-medium">
                  Registration Progress: Step {currentStep + 1} of {totalSteps}
                </span>
                <p className="mt-1 text-sm text-blue-600">
                  Your registration is automatically saved at each step. You can return anytime to continue.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Debug Section */}
        {registrationData && (
          <div>
            <button
              onClick={() => setShowDebug(!showDebug)}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                isDarkMode
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-white/10'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'
              }`}
            >
              {showDebug ? 'Hide' : 'Show'} Registration Debug
            </button>
            
            {showDebug && (
              <div className="mt-4">
                <RegistrationDebug
                  eventId={event?.id}
                  userId={registrationData?.userId}
                  eventData={event}
                  isDarkMode={isDarkMode}
                />
              </div>
            )}
          </div>
        )}

        {/* Render standardized navigation */}
        {renderNavigation && renderNavigation(handleContinue, null, true, getNextStepLabel())}
      </div>
    </div>
  );
};

export default EventOverview;
